import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { X, AlertTriangle, ArrowRight, Hand } from "lucide-react";
import { api, formatApiError, taskHref, CLASS_STYLE, PRIORITY_LABELS, TASK_STATUS_LABELS, BOARD_STATUS_LABELS } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { toast } from "sonner";

const LOC_LABEL = { backlog: "No Backlog", kanban: "No Kanban", impediment: "Impedimento/Problema", archived: "Arquivada" };

// Generic list window behind a clickable Dashboard card. Each task opens its REAL location.
export default function DashboardTaskListModal({ bucket, title, showImpediment, onClose }) {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [tasks, setTasks] = useState(null);
  const [error, setError] = useState("");

  const fetchTasks = () => api.get(`/dashboard/tasks`, { params: { bucket } })
    .then(({ data }) => setTasks(data.tasks))
    .catch((e) => setError(formatApiError(e)));
  useEffect(() => { fetchTasks(); /* eslint-disable-next-line */ }, [bucket]);

  const open = (t) => { onClose(); navigate(taskHref(t)); };
  const claim = async (t, e) => {
    e.stopPropagation();
    try { await api.post(`/tasks/${t.id}/claim`); toast.success("Tarefa assumida."); fetchTasks(); }
    catch (err) { toast.error(formatApiError(err)); }
  };
  const canClaim = (t) => user?.role !== "viewer" && t.available_to_claim && !t.assignee_id;
  const fmt = (d) => d ? new Date(d).toLocaleDateString("pt-BR") : "—";
  const locLabel = (t) => t.location === "kanban" && t.board_column_key
    ? `No Kanban — ${BOARD_STATUS_LABELS[t.board_column_key] || t.board_column_key}` : (LOC_LABEL[t.location] || "—");

  return (
    <div className="fixed inset-0 z-[70] bg-slate-900/40 grid place-items-center p-4" onClick={onClose} data-testid="dashboard-task-modal">
      <div onClick={(e) => e.stopPropagation()} className="bg-white rounded-xl border border-slate-200 shadow-xl w-full max-w-4xl max-h-[85vh] flex flex-col">
        <div className="flex items-center justify-between px-6 py-4 border-b border-slate-200">
          <h3 className="font-heading text-xl font-semibold">{title}</h3>
          <button onClick={onClose} data-testid="dashboard-modal-close" className="p-1.5 rounded-md hover:bg-slate-100"><X className="w-5 h-5" /></button>
        </div>
        <div className="overflow-y-auto">
          {error && <div className="p-4 text-sm text-red-700">{error}</div>}
          {tasks === null ? <div className="p-8 text-center text-sm text-slate-500">Carregando...</div>
            : tasks.length === 0 ? <div className="p-12 text-center text-sm text-slate-500">Nenhuma tarefa.</div>
            : (
              <table className="w-full text-sm">
                <thead><tr className="text-left text-[11px] font-bold uppercase tracking-wider text-slate-500 border-b border-slate-200 sticky top-0 bg-white">
                  <th className="px-5 py-2.5">Tarefa</th><th className="px-2 py-2.5 w-16">Escopo</th>
                  <th className="px-2 py-2.5 w-36">Projeto</th><th className="px-2 py-2.5 w-32">Responsável</th>
                  <th className="px-2 py-2.5 w-24">Prioridade</th><th className="px-2 py-2.5 w-24">Prazo</th>
                  <th className="px-2 py-2.5 w-40">Local</th><th className="px-2 py-2.5 w-8"></th>
                </tr></thead>
                <tbody className="divide-y divide-slate-100">
                  {tasks.map((t) => (
                    <tr key={t.id} onClick={() => open(t)} data-testid={`dash-modal-row-${t.id}`} className="hover:bg-slate-50/60 cursor-pointer">
                      <td className="px-5 py-2.5">
                        <div className="font-medium text-slate-900">{t.title}</div>
                        {showImpediment && t.impediment_reason && <div className="text-xs text-amber-700 flex items-center gap-1 mt-0.5"><AlertTriangle className="w-3 h-3" /> {t.impediment_reason}</div>}
                        {t.available_to_claim && <span className="text-[10px] text-emerald-600 font-semibold">Disponível para assumir</span>}
                      </td>
                      <td className="px-2 py-2.5"><span className={`inline-flex items-center justify-center w-7 h-7 rounded-md text-xs font-semibold ${CLASS_STYLE[t.classification]}`}>{t.classification}</span></td>
                      <td className="px-2 py-2.5 text-slate-600"><div className="truncate">{t.project_name}</div><div className="text-xs text-slate-400 truncate">{t.workspace_name}</div></td>
                      <td className="px-2 py-2.5 text-slate-600">{t.assignee_name || "—"}</td>
                      <td className="px-2 py-2.5 text-slate-600">{PRIORITY_LABELS[t.priority]}</td>
                      <td className="px-2 py-2.5 text-slate-600">{fmt(t.due_date)}</td>
                      <td className="px-2 py-2.5 text-slate-500 text-xs">{locLabel(t)}</td>
                      <td className="px-2 py-2.5 text-right">
                        {canClaim(t)
                          ? <button onClick={(e) => claim(t, e)} data-testid={`dash-claim-${t.id}`} className="inline-flex items-center gap-1 h-7 px-2 rounded-md bg-emerald-600 text-white text-xs hover:bg-emerald-700"><Hand className="w-3.5 h-3.5" /> Assumir</button>
                          : <ArrowRight className="w-4 h-4 text-slate-300 inline" />}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
        </div>
      </div>
    </div>
  );
}
